import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Row,
  Col,
  Spinner
} from 'reactstrap';
import instance from '../../api/Axios';

const BeneficiaryEntitlements = () => {
  const { beneficiaryId } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [beneficiary, setBeneficiary] = useState(null);
  const [components, setComponents] = useState([]);
  const [beneficiaryEntitlements, setBeneficiaryEntitlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);

  // Fetch beneficiary details and components
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');

        // Fetch beneficiary details
        const beneficiaryResponse = await instance.get(`/api/accounts/beneficiaries/${beneficiaryId}/`);
        const beneficiaryData = beneficiaryResponse.data;
        setBeneficiary(beneficiaryData);

        // Fetch components for this bursary
        if (beneficiaryData.bursary) {
          const componentsResponse = await instance.get(`/api/accounts/bursaries/${beneficiaryData.bursary}/components/`);
          setComponents(componentsResponse.data || []);

          // Fetch beneficiary's current entitlements
          try {
            const entitlementsResponse = await instance.get(`/api/accounts/beneficiaries/${beneficiaryId}/entitlements/`);
            setBeneficiaryEntitlements(entitlementsResponse.data || []);
          } catch (entitlementError) {
            // If entitlements endpoint doesn't exist yet, start with empty array
            setBeneficiaryEntitlements([]);
          }
        }
      } catch (err) {
        console.error('Error fetching beneficiary data:', err);
        setError('Failed to load beneficiary data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [beneficiaryId]);



  const isEntitlementSelected = (componentId) => {
    return beneficiaryEntitlements.some(entitlement => 
      (entitlement.component_id === componentId) || (entitlement.component === componentId)
    );
  };

  const toggleEntitlement = async (component, isSelected) => {
    try {
      setUpdating(true);
      
      if (isSelected) {
        // Add entitlement
        const response = await instance.post(`/api/accounts/beneficiaries/${beneficiaryId}/entitlements/`, {
          component: component.id
        });
        
        setBeneficiaryEntitlements(prev => [...prev, response.data]);
      } else {
        // Remove entitlement
        const entitlement = beneficiaryEntitlements.find(e => 
          (e.component_id === component.id) || (e.component === component.id)
        );
        if (entitlement) {
          await instance.delete(`/api/accounts/beneficiaries/${beneficiaryId}/entitlements/${entitlement.id}/`);
          setBeneficiaryEntitlements(prev => prev.filter(e => e.id !== entitlement.id));
        }
      }
    } catch (err) {
      console.error('Error toggling entitlement:', err);
      setError('Failed to update entitlement. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Container fluid>
        <Row>
          <Col>
            <Card>
              <CardBody>
                <div className="text-center py-5">
                  <Spinner color="primary" size="lg" />
                  <p className="mt-3">Loading beneficiary entitlements...</p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid>
        <Row>
          <Col>
            <Card>
              <CardBody>
                <Alert color="danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error}
                </Alert>
                <div className="text-center">
                  <Button color="primary" onClick={() => window.location.reload()}>
                    <i className="fas fa-refresh me-2"></i>
                    Try Again
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row>
        <Col>
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-0">
                  <i className="fas fa-list-check me-2"></i>
                  Beneficiary Entitlements
                </h4>
                {beneficiary && (
                  <small className="text-muted">
                    {beneficiary.student_name} - {beneficiary.bursary_name}
                  </small>
                )}
              </div>
              <Button 
                color="secondary" 
                size="sm" 
                onClick={() => navigate(`/general-dashboard/beneficiaries/view/${beneficiaryId}`)}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back to Beneficiary
              </Button>
            </CardHeader>
            <CardBody>
              {!beneficiary ? (
                <Alert color="warning">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Beneficiary not found.
                </Alert>
              ) : components.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-puzzle-piece fa-4x text-muted mb-3"></i>
                  <h5>No Components Available</h5>
                  <p className="text-muted">
                    This bursary has no components configured. 
                    Contact your administrator to add components to the bursary.
                  </p>
                </div>
              ) : (
                <div>
                  <div className="mb-4">
                    <h5>
                      <i className="fas fa-info-circle me-2 text-info"></i>
                      Manage Student Entitlements
                    </h5>
                    <p className="text-muted">
                      Select which components this student is entitled to receive from their bursary. 
                      Checked items indicate the student has access to that component.
                    </p>
                  </div>

                  {updating && (
                    <Alert color="info" className="mb-4">
                      <i className="fas fa-spinner fa-spin me-2"></i>
                      Updating entitlements...
                    </Alert>
                  )}

                  <Row>
                    {components.map((component) => {
                      const isSelected = isEntitlementSelected(component.id);
                      return (
                        <Col md={6} lg={4} key={component.id} className="mb-3">
                          <Card 
                            className={`h-100 ${isSelected ? 'border-success bg-light' : 'border-light'}`}
                            style={{ 
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onClick={() => !updating && toggleEntitlement(component, !isSelected)}
                          >
                            <CardBody className="d-flex align-items-start">
                              <div className="me-3 mt-1">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={isSelected}
                                  onChange={(e) => toggleEntitlement(component, e.target.checked)}
                                  disabled={updating}
                                  style={{ pointerEvents: 'none' }}
                                />
                              </div>
                              <div className="flex-grow-1">
                                <h6 className="mb-2 d-flex align-items-center">
                                  {component.name}
                                  <Badge 
                                    color={isSelected ? 'success' : 'secondary'} 
                                    className="ms-2"
                                    pill
                                  >
                                    {isSelected ? 'Entitled' : 'Not Entitled'}
                                  </Badge>
                                </h6>
                                <small className="text-muted">
                                  Type: {component.component_type_name}
                                </small>
                                {component.description && (
                                  <p className="small mb-0 mt-2">
                                    {component.description}
                                  </p>
                                )}
                                {component.amount && (
                                  <div className="mt-2">
                                    <small className="text-success fw-bold">
                                      {component.currency} {component.amount} ({component.frequency})
                                    </small>
                                  </div>
                                )}
                              </div>
                            </CardBody>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>

                  <div className="mt-4 p-3 bg-light rounded">
                    <h6 className="mb-2">
                      <i className="fas fa-check-circle me-2 text-success"></i>
                      Current Entitlements Summary
                    </h6>
                    {beneficiaryEntitlements.length === 0 ? (
                      <small className="text-muted">
                        This student has no entitlements selected.
                      </small>
                    ) : (
                      <div>
                        <small className="text-muted">
                          This student is entitled to {beneficiaryEntitlements.length} component(s):
                        </small>
                        <div className="mt-2">
                          {beneficiaryEntitlements.map((entitlement, index) => {
                            // Handle both component_id (from simple serializer) and component (from full serializer)
                            const componentId = entitlement.component_id || entitlement.component;
                            const component = components.find(c => c.id === componentId);
                            return (
                              <Badge key={index} color="success" className="me-2 mb-2">
                                {component?.name || entitlement.component_name || 'Unknown Component'}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default BeneficiaryEntitlements;